import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
	Category, CriteriaDetails, Criterion, Period, PeriodTime, Profile, Rate, ValidationFlags,
	CRITERIA_TYPES, CURRENCY, DAYS, MONTH_DAYS,
	DAY_SECONDS, HOUR_SECONDS, MINUTE_SECONDS,
	MAX_CRITERIA, MAX_NAME_LENGTH, MAX_PERIODS, MAX_RATES, YEARS_AHEAD
} from './multi-tariff.model';

@Injectable()
export class MultiTariffService {

	route = '/multi-tariff/';

	readonly maxRates = MAX_RATES;
	readonly maxPeriods = MAX_PERIODS;
	readonly maxCriteria = MAX_CRITERIA;

	constructor(private httpClient: HttpClient) { }

	getAllProfiles() {
		return this.httpClient.get(this.route + 'profiles', { observe: 'body' });
	}

	getProfileInfo(profileId: number) {
		return this.httpClient.get(this.route + 'get-profile/' + profileId, { observe: 'body' });
	}

	getProfileSites(profileId: number) {
		return this.httpClient.get(this.route + 'get-profile-sites/' + profileId, { observe: 'body' });
	}

	save(profile: Profile, rates: Rate[], categories: Category[]) {
		return this.httpClient.post(this.route + 'save', { profile, rates, categories }, { observe: 'body' });
	}

	deleteProfile(profileId: number) {
		return this.httpClient.delete(this.route + 'delete-profile/' + profileId, { observe: 'body' });
	}

	replaceProfile(newProfileId: number | null, oldProfileId: number) {
		return this.httpClient.post(this.route + 'replace-profile', { newProfileId, oldProfileId }, { observe: 'body' });
	}

	getCurrencyName(currencyId: number): string {
		const currency = CURRENCY.find((item) => item.id === currencyId);
		return currency ? currency.name : '';
	}

	getMonthDays(month: number): number[] {
		if (!month || month < 1 || month > MONTH_DAYS.length)
			return [];

		const days = [];
		for (let day = 1; day <= MONTH_DAYS[month - 1]; day++)
			days.push(day);

		return days;
	}

	getYears(): number[] {
		const currentYear = new Date().getFullYear();
		const years = [];

		for (let year = currentYear; year <= currentYear + YEARS_AHEAD; year++)
			years.push(year);

		return years;
	}

	sortPeriods(periods: Period[]) {
		periods.sort((a: Period, b: Period) => Number(a.from) - Number(b.from));
	}

	mapTimeToSeconds(time: PeriodTime, isEndOfPeriod?: boolean): number {
		if (!time && time !== 0)
			return null;

		if (typeof time === 'number')
			return time;

		if (isEndOfPeriod && time === '00:00')
			return DAY_SECONDS;

		const parts = String(time).split(':');
		return (+parts[0] * HOUR_SECONDS) + (+parts[1] * MINUTE_SECONDS);
	}

	mapSecondsToTime(seconds: number): string {
		const hour = Math.floor(seconds / HOUR_SECONDS);
		const minute = Math.floor((seconds - (hour * HOUR_SECONDS)) / MINUTE_SECONDS);

		return String(hour % 24).padStart(2, '0') + ':' + String(minute).padStart(2, '0');
	}

	mapRatesAfterLoad(rates: Rate[]) {
		for (const rate of rates) {
			rate.periods = rate.periods || [];
			this.sortPeriods(rate.periods);

			for (const period of rate.periods) {
				period.from = this.mapSecondsToTime(Number(period.from));
				period.to = this.mapSecondsToTime(Number(period.to));
			}
		}
	}

	mapCategoryRatesAfterLoad(categories: Category[], rates: Rate[]) {
		const indexByRateId = {};

		rates.forEach((rate, index) => {
			if (rate.id)
				indexByRateId[rate.id] = index;
		});

		for (const category of categories) {
			const index = indexByRateId[category.rate_id];
			category.rate_id = index === undefined ? null : index;
		}
	}

	isValidName(name: unknown): boolean {
		return typeof name === 'string' && !!name.trim().length && name.length <= MAX_NAME_LENGTH;
	}

	isValidInt(value: unknown, min: number, max: number): boolean {
		return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
	}

	hasInvalidField(invalidFields: ValidationFlags): boolean {
		if (invalidFields === true)
			return true;

		if (!invalidFields || typeof invalidFields !== 'object')
			return false;

		return Object.keys(invalidFields).some((key) => this.hasInvalidField(invalidFields[key]));
	}

	saveValidation(profile: Profile, rates: Rate[], categories: Category[]) {
		for (const rate of rates) {
			for (const period of rate.periods || []) {
				period.from = this.mapTimeToSeconds(period.from);
				period.to = this.mapTimeToSeconds(period.to, true);
			}
		}

		const invalidFields = {
			profile: this.validateProfile(profile),
			rates: this.validateRates(rates),
			categories: this.validateCategories(categories, rates)
		};

		return { invalidFields, data: { profile, rates, categories } };
	}

	validateProfile(profile: Profile) {
		const invalidFields: ValidationFlags = {};

		if (!this.isValidName(profile.name))
			invalidFields.name = true;

		if (!this.isValidInt(profile.currency, 1, CURRENCY.length))
			invalidFields.currency = true;

		return invalidFields;
	}

	validatePeriods(periods: Period[]) {
		if (!periods || !periods.length || periods.length > MAX_PERIODS)
			return { minOrMax: true };

		const invalidPeriods: ValidationFlags = {};
		let hasInvalidPeriod = false;

		periods.forEach((period, index) => {
			const invalidPeriod: ValidationFlags = {};

			if (!this.isValidName(period.name))
				invalidPeriod.name = true;

			if (!this.isValidInt(period.from, 0, DAY_SECONDS))
				invalidPeriod.from = true;

			if (!this.isValidInt(period.to, 0, DAY_SECONDS))
				invalidPeriod.to = true;

			if (typeof period.price !== 'number' || isNaN(period.price) || period.price < 0)
				invalidPeriod.price = true;

			if (Object.keys(invalidPeriod).length) {
				invalidPeriods[index] = invalidPeriod;
				hasInvalidPeriod = true;
			}
		});

		if (hasInvalidPeriod)
			return invalidPeriods;

		const bounds = periods.map((period) => ({ from: Number(period.from), to: Number(period.to) }));

		let coveredSeconds = 0;
		for (const bound of bounds) {
			if (bound.from === bound.to)
				coveredSeconds = DAY_SECONDS;
			else if (bound.from > bound.to)
				coveredSeconds += DAY_SECONDS - bound.from + bound.to;
			else
				coveredSeconds += bound.to - bound.from;
		}

		if (coveredSeconds < DAY_SECONDS)
			return { fullDay: true };

		if (coveredSeconds > DAY_SECONDS)
			return { overlap: true };

		if (bounds.length > 1) {
			for (const bound of bounds) {
				let meetsBefore = false;
				let meetsAfter = false;

				for (const other of bounds) {
					if (other === bound)
						continue;

					if (bound.from === other.to || (bound.from === 0 && other.to === DAY_SECONDS))
						meetsBefore = true;

					if (bound.to === other.from || (other.from === 0 && bound.to === DAY_SECONDS))
						meetsAfter = true;
				}

				if (!meetsBefore || !meetsAfter)
					return { overlap: true };
			}
		}

		return {};
	}

	validateRates(rates: Rate[]) {
		if (!rates || !rates.length)
			return {};

		if (rates.length > MAX_RATES)
			return { minOrMax: true };

		const invalidFields: ValidationFlags = {};

		rates.forEach((rate, index) => {
			const invalidRate: ValidationFlags = {};

			if (!this.isValidName(rate.name))
				invalidRate.name = true;

			const invalidPeriods = this.validatePeriods(rate.periods);
			if (Object.keys(invalidPeriods).length)
				invalidRate.periods = invalidPeriods;

			if (Object.keys(invalidRate).length)
				invalidFields[index] = invalidRate;
		});

		return invalidFields;
	}

	validateCriteriaDetails(type: number, details: CriteriaDetails) {
		const invalidDetails: ValidationFlags = {};

		if (!details || typeof details !== 'object')
			return true;

		switch (type) {
			case CRITERIA_TYPES.PERIOD_OF_YEAR: {
				const from = details.from || {};
				const to = details.to || {};
				const invalidFrom: ValidationFlags = {};
				const invalidTo: ValidationFlags = {};

				if (!this.isValidInt(from.month, 1, MONTH_DAYS.length))
					invalidFrom.month = true;
				if (!this.getMonthDays(from.month).includes(from.day))
					invalidFrom.day = true;
				if (!this.isValidInt(to.month, 1, MONTH_DAYS.length))
					invalidTo.month = true;
				if (!this.getMonthDays(to.month).includes(to.day))
					invalidTo.day = true;

				if (Object.keys(invalidFrom).length)
					invalidDetails.from = invalidFrom;
				if (Object.keys(invalidTo).length)
					invalidDetails.to = invalidTo;

				if (Object.keys(invalidDetails).length)
					return invalidDetails;

				if (from.month > to.month)
					return { from: { month: true }, to: { month: true } };

				if (from.month === to.month && from.day >= to.day)
					return { from: { day: true }, to: { day: true } };

				return {};
			}

			case CRITERIA_TYPES.DAYS_OF_WEEK: {
				if (!Array.isArray(details.days) || !details.days.length)
					return { days: true };

				const validDays = Object.values(DAYS);
				if (details.days.some((day: number) => !validDays.includes(day)))
					return { days: true };

				return {};
			}

			case CRITERIA_TYPES.FIXED_DATE: {
				const date = details.date || {};
				const invalidDate: ValidationFlags = {};

				if (!this.isValidInt(date.month, 1, MONTH_DAYS.length))
					invalidDate.month = true;
				else if (!this.getMonthDays(date.month).includes(date.day))
					invalidDate.day = true;

				if (Object.keys(invalidDate).length)
					return { date: invalidDate };

				return {};
			}

			case CRITERIA_TYPES.FIRST_DAY_OF_MONTH:
			case CRITERIA_TYPES.SECOND_DAY_OF_MONTH:
			case CRITERIA_TYPES.THIRD_DAY_OF_MONTH:
			case CRITERIA_TYPES.FORTH_DAY_OF_MONTH:
			case CRITERIA_TYPES.LAST_DAY_OF_MONTH: {
				if (!this.isValidInt(details.month, 1, MONTH_DAYS.length))
					invalidDetails.month = true;

				if (!this.isValidInt(details.day, 1, Object.keys(DAYS).length))
					invalidDetails.day = true;

				return invalidDetails;
			}

			case CRITERIA_TYPES.YEAR: {
				if (!this.getYears().includes(details.year))
					return { year: true };

				return {};
			}

			default:
				return true;
		}
	}

	validateCriteria(criteria: Criterion[]) {
		if (!criteria || !criteria.length || criteria.length > MAX_CRITERIA)
			return { minOrMax: true };

		const invalidFields: ValidationFlags = {};
		const validTypes = Object.values(CRITERIA_TYPES);

		criteria.forEach((criterion, index) => {
			const invalidCriterion: ValidationFlags = {};

			if (!this.isValidName(criterion.name))
				invalidCriterion.name = true;

			if (!validTypes.includes(criterion.type))
				invalidCriterion.type = true;
			else {
				const invalidDetails = this.validateCriteriaDetails(criterion.type, criterion.details);
				if (this.hasInvalidField(invalidDetails))
					invalidCriterion.details = invalidDetails;
			}

			if (Object.keys(invalidCriterion).length)
				invalidFields[index] = invalidCriterion;
		});

		return invalidFields;
	}

	validateCategories(categories: Category[], rates: Rate[]) {
		if (!categories || !categories.length)
			return {};

		const invalidFields: ValidationFlags = {};
		const ratesCount = rates ? rates.length : 0;

		categories.forEach((category, index) => {
			const invalidCategory: ValidationFlags = {};

			if (!this.isValidName(category.name))
				invalidCategory.name = true;

			// rate_id is an index into the rates being saved
			if (!ratesCount || !this.isValidInt(category.rate_id, 0, ratesCount - 1))
				invalidCategory.rate_id = true;

			const invalidCriteria = this.validateCriteria(category.criteria);
			if (this.hasInvalidField(invalidCriteria))
				invalidCategory.criteria = invalidCriteria;

			if (Object.keys(invalidCategory).length)
				invalidFields[index] = invalidCategory;
		});

		return invalidFields;
	}
}
